// Multiplayer match view — owns the mp sim, match-server connection,
// intent relay, and full game UI. Rendered by App.tsx when gameMode is
// 'multiplayer-match'.
//
// All state setup + intent-relay wrapping lives in useMpMatchState; this file
// is mostly JSX.

import { MilestoneToastStack } from '../../cinematics/MilestoneToastStack.js';
import { useCinematicDirector } from '../../cinematics/useCinematicDirector.js';
import { ErrorBoundary } from '../../components/ErrorBoundary.js';
import { FooterHint } from '../../components/FooterHint.js';
import { HelpModal } from '../../components/HelpModal.js';
import { TopBar } from '../../components/TopBar.js';
import { useDiplomacyViews } from '../../dynasty/useDiplomacyViews.js';
import { useTreatyNews } from '../../dynasty/useTreatyNews.js';
import { useEspionageView } from '../../espionage/useEspionageView.js';
import { Scene } from '../../render/Scene.js';
import { readRenderStats } from '../../render/renderStats.js';
import { SettingsModal } from '../../settings/SettingsModal.js';
import { watchLink } from '../../spectate/watchLink.js';
import { AttackToastStack } from '../../ui/AttackToastStack.js';
import { DiplomacyToasts } from '../../ui/DiplomacyToasts.js';
import { DoctrinePanel } from '../../ui/DoctrinePanel/index.js';
import { RightPanel } from '../../ui/RightPanel/index.js';
import { TileTooltip } from '../../ui/TileTooltip.js';
import { VictoryBanner } from '../../ui/VictoryBanner.js';
import { DockedDebugHud } from '../../ui/layout/DockedDebugHud.js';
import type { MatchInfo } from '../types.js';
import { ChatOverlay } from './ChatOverlay.js';
import { absentMessage } from './pendingIntents.js';
import { useMpMatchState } from './useMpMatchState.js';

const LOCAL_PLAYER_INDEX = 0;

interface Props {
  matchInfo: MatchInfo;
  onBackToMenu: () => void;
  onRematch?: () => void;
}

export function MultiplayerMatchView({ matchInfo, onBackToMenu, onRematch }: Props) {
  const s = useMpMatchState(matchInfo, onBackToMenu, onRematch);
  const { mpSim, mpMatch } = s;
  const views = useDiplomacyViews(mpSim.snapshot);
  const espionage = useEspionageView(mpSim.snapshot, LOCAL_PLAYER_INDEX);
  const treatyDesk = useTreatyNews(
    views.diplomacy,
    views.dynasty,
    Number(mpSim.tick ?? 0),
    LOCAL_PLAYER_INDEX,
    s.wrappedSim.treaty,
  );
  // Milestones in multiplayer: a toast, never a pause (pacing spec §6 C2).
  const cinematics = useCinematicDirector({
    mode: 'multiplayer',
    enabled: mpSim.snapshot !== null,
    events: mpSim.events ?? [],
    tick: Number(mpSim.tick ?? 0),
    snapshot: mpSim.snapshot,
    localPlayer: LOCAL_PLAYER_INDEX,
  });

  const absentText = absentMessage(
    mpMatch.absent.map(
      (id) => matchInfo.players.find((p) => p.accountId === id)?.displayName ?? 'A player',
    ),
  );

  if (mpSim.status === 'loading') return null;
  if (mpSim.status === 'error') return <div className="p-8 text-red-400">Failed to load sim</div>;
  if (mpSim.snapshot === null) return null;

  return (
    <ErrorBoundary>
      <div className="relative h-full w-full">
        <TopBar
          food={mpSim.localTreasury.food}
          industry={mpSim.localTreasury.industry}
          knowledge={mpSim.localTreasury.knowledge}
          faith={mpSim.localTreasury.faith}
          treasury={mpSim.localTreasury.gold}
          tick={mpSim.tick}
          runState={mpSim.runState}
          speed={mpSim.speed}
          researchState={mpSim.researchState}
          faithCrisis={mpSim.faithCrisis}
          startResearch={s.wrappedSim.startResearch}
          pilgrimage={s.wrappedSim.pilgrimage}
          reform={s.wrappedSim.reform}
          pause={mpSim.pause}
          resume={mpSim.resume}
          setSpeed={mpSim.setSpeed}
          onOpenSettings={() => s.setSettingsOpen(true)}
          onOpenHelp={() => s.setHelpOpen(true)}
        />
        <Scene
          snapshot={mpSim.snapshot}
          onTileHover={s.setHoveredTile}
          onTileClick={s.handleTileClick}
          onCityClick={s.handleCityClick}
          onUnitClick={s.handleUnitClick}
          onMoveClick={s.handleMoveClick}
          onBackgroundClick={s.handleBackgroundClick}
          selectedTileId={s.selection?.kind === 'tile' ? s.selection.id : null}
          selectedCityId={s.selection?.kind === 'city' ? s.selection.id : null}
          selectedUnitId={s.selection?.kind === 'unit' ? s.selection.id : null}
        />
        <DoctrinePanel
          state={mpSim.localPlayer}
          currentTick={mpSim.tick}
          onInvest={s.wrappedSim.invest}
        />
        {/*
          RightPanel is the persistent right-rail panel in MP matches. Mirrors
          App.tsx — always mounts; the Selection tab appears conditionally
          inside the panel when `s.selected` becomes truthy.
        */}
        <RightPanel
          selected={s.selected}
          snapshot={mpSim.snapshot}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          matchOver={mpSim.victory !== null}
          garrisonCount={s.garrisonCount}
          buildings={s.buildings}
          treasuryIndustry={mpSim.localTreasury.industry}
          onAttack={s.wrappedSim.attackCity}
          onDeploy={s.handleDeployGarrison}
          onStartBuilding={s.handleStartBuilding}
          onAcceptMerchant={s.wrappedSim.acceptMerchant}
          onDeclineMerchant={s.wrappedSim.declineMerchant}
          onSetDoctrine={s.wrappedSim.setCityDoctrine}
          dynasty={views.dynasty}
          espionage={espionage}
          onDispatchSpy={s.wrappedSim.espionage.dispatchSpy}
          opsActions={s.wrappedSim.espionage}
          onChooseCapture={s.wrappedSim.espionage.chooseCapture}
          onSpyRescue={s.wrappedSim.espionage.spyRescue}
          onRaidRescue={s.wrappedSim.espionage.raidRescue}
          onPayRansom={s.wrappedSim.espionage.payRansom}
          diplomacy={views.diplomacy}
          onProposeMarriage={s.wrappedSim.dynasty.propose}
          onProposeTreaty={s.wrappedSim.treaty.propose}
          onAnswerTreaty={(from, accept) => treatyDesk.answer(from, accept)}
          onDeclareWar={s.wrappedSim.treaty.declareWar}
          suggestions={[]}
          onApplySuggestion={() => {}}
          onActivateAbility={() => {}}
          onSelectAbility={() => {}}
          onSetFormation={s.wrappedSim.setFormation}
          onClose={s.closeSelection}
          tick={mpSim.tick}
          events={mpSim.events ?? []}
          // TODO(right-panel-victory-projection): sim exposes VictoryProgressState
          // (tick-milestone shape); RightPanel wants VictoryProgress
          // (per-player wonder/domination/faith %). No projection exists yet —
          // OverviewTab renders its empty state until one is added.
          victoryProgress={null}
          localPlayer={mpSim.localPlayer ?? null}
          playerHudEntries={mpSim.playerHudEntries}
          // TODO(right-panel-mp-colors): useMpMatchState does not yet expose a
          // playerColors map; pass empty until a derivation lands. DiplomacyTab
          // renders uncolored rows in MP mode as a result.
          playerColors={new Map<number, string>()}
        />
        {mpSim.victory && (
          <VictoryBanner
            winner={mpSim.victory.winner}
            path={mpSim.victory.path}
            localPlayerIndex={LOCAL_PLAYER_INDEX}
            onNewMatch={s.startNewMatch}
          />
        )}
        <AttackToastStack toasts={s.attackToasts} onDismiss={s.dismissToast} />
        <DiplomacyToasts
          snapshot={mpSim.snapshot}
          players={mpSim.playerHudEntries}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          desk={treatyDesk}
          dynasty={s.wrappedSim.dynasty}
        />
        <MilestoneToastStack director={cinematics.director} localPlayer={LOCAL_PLAYER_INDEX} />
        <TileTooltip tile={s.hoveredTile} />
        <DockedDebugHud
          visible={s.showDebug}
          tick={mpSim.tick}
          entityCount={mpSim.tileCount + mpSim.cityCount}
          determinismHash={mpSim.determinismHash}
          wsState={mpSim.wsState}
          getRenderStats={readRenderStats}
          // Top-left of the centre column, clear of both rails (same slot as
          // the single-player match view). Desktop only.
          positionClassName="fixed top-[var(--hud-panel-top)] right-auto left-[var(--centre-inset)] z-30 hidden opacity-90 lg:block"
        />
        <FooterHint />

        {/* End-of-match result banner — the match-server only started
            emitting match:end with the ranked slice; the player picks their
            exit instead of being teleported to the menu. */}
        {s.matchEnded && !mpSim.victory && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60"
            data-testid="mp-match-end"
          >
            <div className="flex flex-col items-center gap-4 rounded-lg bg-panel-scrim px-10 py-8 text-foreground shadow-xl backdrop-blur">
              <h2 className="text-2xl font-bold">
                {s.matchEnded.winnerId === matchInfo.localPlayerId
                  ? 'Victory!'
                  : s.matchEnded.winnerId === ''
                    ? 'Match Over'
                    : 'Defeat'}
              </h2>
              {s.matchEnded.winnerId && s.matchEnded.winnerId !== matchInfo.localPlayerId && (
                <p className="text-sm text-muted-foreground">
                  {matchInfo.players.find((p) => p.accountId === s.matchEnded?.winnerId)
                    ?.displayName ?? 'Opponent'}{' '}
                  wins
                </p>
              )}
              <div className="flex gap-3">
                {onRematch && (
                  <button
                    type="button"
                    className="rounded bg-emerald-600 px-5 py-2 font-bold text-white hover:bg-emerald-500"
                    onClick={onRematch}
                  >
                    Play Again
                  </button>
                )}
                <button
                  type="button"
                  className="rounded bg-sky-600 px-5 py-2 font-bold text-white hover:bg-sky-500"
                  onClick={onBackToMenu}
                  data-testid="mp-match-end-menu"
                >
                  Back to Menu
                </button>
              </div>
            </div>
          </div>
        )}
        {mpMatch.reconnecting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45 dark:bg-black/50">
            <div className="rounded-lg bg-panel-scrim px-6 py-4 text-foreground shadow-lg backdrop-blur">
              <p className="text-lg font-medium">Reconnecting...</p>
              <p className="text-sm text-muted-foreground">
                Please wait while we sync your game state
              </p>
            </div>
          </div>
        )}

        {/* Another seat dropped: the lockstep carries on without them. */}
        {absentText && (
          <output
            className="absolute left-1/2 top-16 z-50 -translate-x-1/2 rounded bg-amber-500/15 px-3 py-1.5 text-xs text-amber-700 backdrop-blur dark:bg-yellow-800/80 dark:text-yellow-200"
            data-testid="mp-absent-banner"
          >
            {absentText}
          </output>
        )}
        {/* Replay could not bring us level with the match: nothing to step. */}
        {mpMatch.resyncNeeded && !s.matchEnded && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60">
            <div
              className="flex max-w-sm flex-col items-center gap-3 rounded-lg bg-panel-scrim px-6 py-5 text-center text-foreground shadow-xl backdrop-blur"
              data-testid="mp-resync-needed"
              role="alertdialog"
            >
              <p className="text-lg font-medium">Out of sync with the match</p>
              <p className="text-sm text-muted-foreground">
                You were away too long to catch up. The match goes on without you; leave it and
                rejoin from the menu.
              </p>
              <button
                type="button"
                className="rounded bg-sky-600 px-5 py-2 font-bold text-white hover:bg-sky-500"
                onClick={onBackToMenu}
              >
                Back to Menu
              </button>
            </div>
          </div>
        )}

        {/* Multiplayer-specific: match connection status + chat */}
        <div className="absolute bottom-4 right-4 z-50 flex items-center gap-2">
          {!mpMatch.connected && (
            <span className="rounded bg-amber-500/15 px-2 py-1 text-xs text-amber-700 dark:bg-yellow-800/80 dark:text-yellow-200">
              Connecting to match…
            </span>
          )}
          {mpMatch.pending > 0 && (
            <span
              className="rounded bg-sky-500/15 px-2 py-1 text-xs text-sky-700 dark:bg-sky-800/70 dark:text-sky-200"
              data-testid="mp-sent-pips"
              title="Orders sent, waiting for the next turn"
            >
              {'●'.repeat(Math.min(mpMatch.pending, 5))}
              {mpMatch.pending > 5 ? '+' : ''} sent
            </span>
          )}
          {s.determinismMismatch && (
            <span className="rounded bg-amber-500/15 px-2 py-1 text-xs text-amber-700 dark:bg-yellow-800/80 dark:text-yellow-200">
              Sync issue
            </span>
          )}
          <button
            type="button"
            className="rounded border border-border bg-panel-scrim px-3 py-1 text-xs text-foreground hover:bg-accent"
            data-testid="mp-copy-watch-link"
            title="Copy a link a signed-in friend can use to watch this match"
            onClick={() => {
              navigator.clipboard
                ?.writeText(watchLink(window.location.origin, matchInfo.matchId))
                .catch(() => {});
            }}
          >
            Copy watch link
          </button>
          <button
            type="button"
            className="rounded border border-border bg-panel-scrim px-3 py-1 text-xs text-foreground hover:bg-accent"
            onClick={() => s.setChatVisible((v) => !v)}
          >
            Chat (Enter)
          </button>
        </div>
        <ChatOverlay
          chatHistory={mpMatch.chatHistory}
          onSend={mpMatch.sendChat}
          visible={s.chatVisible}
          onToggle={() => s.setChatVisible((v) => !v)}
        />
        <SettingsModal open={s.settingsOpen} onClose={() => s.setSettingsOpen(false)} />
        <HelpModal open={s.helpOpen} onClose={() => s.setHelpOpen(false)} />
      </div>
    </ErrorBoundary>
  );
}
